// "다음부터 묻지 않고 접속" — 뷰어 쪽 기억(어느 기기가 **내 계정**을 비밀번호 없이 허용했는가).
//
// 🔴 **비밀이 아니다.** 비밀번호는 어디에도 저장하지 않는다 — 허용 여부의 진실원은 **기기**다(에이전트 `trusted_viewers`).
//    뷰어가 기억하는 것은 "이 기기는 물어보지 않고 바로 접속해 봐도 된다" 는 표시뿐이라, 이 값이 틀려도(기기가 허용을
//    거뒀다) 접속이 거부될 뿐 보안이 뚫리지 않는다. 거부되면 표시를 지우고 다음부터 다시 비밀번호를 묻는다.
// - 표시는 **계정별**(로그인 토큰의 `sub` = 서버 사용자 ID) — 같은 PC 를 다른 계정이 쓰면 그 계정은 다시 묻는다.
// - 저장소 = localStorage(창끼리 공유 — 세션 창에서 성공을 기록하고 목록 창이 읽는다).

export const TRUSTED_DEVICES_KEY = 'rs.trustedDevices';
/** 기억하는 최대 개수(오래된 것부터 버린다 — 무한히 쌓이지 않게). */
export const TRUSTED_DEVICES_MAX = 200;

/** 저장소(시험에서 바꿔 끼운다). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStore(): KeyValueStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** 로그인 토큰(JWT) → 서버 사용자 ID(`sub`). 형식이 틀리면 null(그 경우 기억 기능을 쓰지 않는다). */
export function tokenSubject(token: string | null | undefined): string | null {
  const part = token?.split('.')[1];
  if (!part) return null;
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '='));
    const sub = (JSON.parse(json) as { sub?: unknown }).sub;
    return typeof sub === 'string' && sub ? sub : null;
  } catch {
    return null;
  }
}

const keyOf = (user: string, target: string) => `${user}|${target}`;

function load(store: KeyValueStore | null): string[] {
  try {
    const v: unknown = JSON.parse(store?.getItem(TRUSTED_DEVICES_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((e): e is string => typeof e === 'string') : [];
  } catch {
    return [];
  }
}

function save(list: string[], store: KeyValueStore | null): void {
  try {
    store?.setItem(TRUSTED_DEVICES_KEY, JSON.stringify(list.slice(-TRUSTED_DEVICES_MAX)));
  } catch {
    // 저장이 막힌 환경 — 매번 비밀번호를 물을 뿐이다.
  }
}

/** 이 계정으로 이 대상(agentId 또는 기기 코드)은 비밀번호 없이 접속해 봐도 되는가. */
export function isTrustedDevice(user: string | null, target: string, store = defaultStore()): boolean {
  return !!user && load(store).includes(keyOf(user, target));
}

/** 기기가 허용했다(비밀번호 + "다음부터 묻지 않고 접속" 으로 접속에 성공). */
export function markTrustedDevice(user: string | null, target: string, store = defaultStore()): void {
  if (!user) return;
  const k = keyOf(user, target);
  save([...load(store).filter((e) => e !== k), k], store);
}

/** 기기가 허용을 거뒀다(비밀번호 없이 접속했더니 거부) — 다음부터 다시 묻는다. */
export function forgetTrustedDevice(user: string | null, target: string, store = defaultStore()): void {
  if (!user) return;
  const k = keyOf(user, target);
  save(load(store).filter((e) => e !== k), store);
}
